import type { WizardData } from "@/pages/CreateCard";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";

interface Props {
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
}



const HelpTip = ({ text }: { text: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle size={14} className="text-muted-foreground/50 cursor-help" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const StepChipIn = ({ data, updateData }: Props) => {
  const { t } = useTranslation();
  const currency = useCurrency();

  const contributionTypes = [
    { value: "any_amount" as const, label: t("wizard.chipIn.anyAmount"), tip: t("wizard.chipIn.anyAmountTip") },
    { value: "set_amount" as const, label: t("wizard.chipIn.exactAmount"), tip: t("wizard.chipIn.exactAmountTip") },
  ];

  const feeOptions = [
    { value: "deduct_from_collection" as const, label: t("wizard.chipIn.deductFromCollection"), desc: t("wizard.chipIn.deductFromCollectionDesc") },
    { value: "organizer_pays" as const, label: t("wizard.chipIn.organizerPays"), desc: t("wizard.chipIn.organizerPaysDesc") },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
          {t("wizard.chipIn.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("wizard.chipIn.subtitle")}
        </p>
      </div>

      {/* Contribution type */}
      <div>
        <Label className="text-sm font-medium text-foreground mb-3 block">
          {t("wizard.chipIn.howMuch")}
        </Label>
        <div className="space-y-2">
          {contributionTypes.map((ct) => (
            <button
              key={ct.value}
              onClick={() => updateData({ contributionType: ct.value })}
              className={`w-full flex items-center justify-between rounded-xl border p-4 transition-all ${
                data.contributionType === ct.value
                  ? "border-foreground bg-foreground/[0.03]"
                  : "border-border hover:border-foreground/30"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  data.contributionType === ct.value ? "border-primary" : "border-border"
                }`}>
                  {data.contributionType === ct.value && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                </div>
                <span className="text-sm text-foreground">{ct.label}</span>
              </div>
              <HelpTip text={ct.tip} />
            </button>
          ))}
        </div>
        {data.contributionType === "set_amount" && (
          <div className="relative mt-3 max-w-[200px]">
            <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currency}</span>
            <Input
              type="number"
              min="1"
              value={data.setAmount}
              onChange={(e) => updateData({ setAmount: e.target.value })}
              placeholder={t("wizard.chipIn.amount")}
              className="ps-12 border-border"
            />
          </div>
        )}
      </div>

      {/* Toggles */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-foreground">{t("wizard.chipIn.signWithout")}</span>
        <Switch
          checked={data.contributionType === "optional"}
          onCheckedChange={(c) => updateData({ contributionType: c ? "optional" : "any_amount" })}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-foreground">{t("wizard.chipIn.setGoal")}</span>
        <Switch
          checked={data.collectionGoal}
          onCheckedChange={(c) => updateData({ collectionGoal: c })}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-foreground">{t("wizard.chipIn.showTotal")}</span>
        <Switch
          checked={data.showTotalCollected}
          onCheckedChange={(c) => updateData({ showTotalCollected: c })}
        />
      </div>

      {/* Goal amount input */}
      {data.collectionGoal && (
        <div className="relative max-w-[200px]">
          <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currency}</span>
          <Input
            type="number"
            min="1"
            value={data.collectionGoalAmount}
            onChange={(e) => updateData({ collectionGoalAmount: e.target.value })}
            placeholder={t("wizard.chipIn.goalAmount")}
            className="ps-12 border-border"
          />
        </div>
      )}

      {/* Collection note */}
      <div>
        <Label className="text-sm font-medium text-foreground mb-2 block">
          {t("wizard.chipIn.collectionNote")}
        </Label>
        <Textarea
          value={data.collectionNote}
          onChange={(e) => updateData({ collectionNote: e.target.value })}
          placeholder={t("wizard.chipIn.collectionNotePlaceholder")}
          className="border-border resize-none"
          rows={2}
        />
      </div>

      <div>
        <Label className="text-sm font-medium text-foreground mb-3 block">
          {t("wizard.chipIn.feeHandling")}
        </Label>
        <div className="space-y-2">
          {feeOptions.map((fo) => (
            <button
              key={fo.value}
              onClick={() => updateData({ feeHandling: fo.value })}
              className={`w-full text-start rounded-xl border p-4 transition-all ${
                data.feeHandling === fo.value
                  ? "border-foreground bg-foreground/[0.03]"
                  : "border-border hover:border-foreground/30"
              }`}
            >
              <div className="text-sm font-medium text-foreground">{fo.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{fo.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StepChipIn;
